import FoodFetcher from 'data/FoodFetcher';
import SendGridPoster from 'data/SendGridPoster';
import type { Bundle, BundleEntry, PlanDefinition, PlanDefinitionAction } from 'fhir/r4';
import type { CareTeam, FhirResource, Observation, Provenance } from 'fhir/r4';
import { FHIRProxyClient } from 'fhir-sdoh';
import {
  EMAIL_TEMPLATES,
  referralOrganizations,
  REFERRAL_SUBJECT_LINES,
  TEXT_MESSAGE_TEMPLATES_PINPOINT,
} from 'fhir-sdoh/common/constants';
import _ from 'lodash';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import {
  Message,
  Button,
  Table,
  Label,
  Loader,
  Dimmer,
  Select,
  Form,
  Dropdown,
  Icon,
  Popup,
} from 'semantic-ui-react';
import type { DropdownProps, SemanticCOLORS } from 'semantic-ui-react';
import useSWR from 'swr';
import type { Fetched, PatientEntry, PatientResource } from 'types';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRFetcher, { MultiFetcher } from '../../../data/FHIRFetcher';
import FHIRUpdater from '../../../data/FHIRUpdater';
import { FHIRUtils as FHIRSDOHUtils, Intervene, ProvenanceBuilder } from '../../../fhir-sdoh';
import type { ApiFetchError } from '../../../services/api-client';
import * as FHIRUtils from '../../../utils/FHIRUtils';
import { sortReducer } from '../../../utils/SortReducer';
import { mapKey } from '../../../utils/mapKey';
import { sortDirection } from '../../../utils/sortDirection';
import { REFERRAL_ID_PREFIX } from '../vendorConfig';

export const REFERRAL_OBSERVATION_CODE = 'REFERRAL-TYPE';

/**
 * One row of the referrals table: the referral flattened by `parseFHIRreferral`,
 * merged with the patient and care-team fields looked up per patient id.
 */
type ReferralRow = {
  OA?: string | undefined;
  RD?: string | undefined;
  id?: string | undefined;
  interventions?: unknown[] | undefined;
  lastUpdatedAge?: string | undefined;
  lastUpdatedBy?: unknown[] | undefined;
  orgId?: unknown[] | undefined;
  patientEmail?: unknown[] | undefined;
  patientId?: string | undefined;
  patientName?: string | undefined;
  patientPhone?: unknown[] | undefined;
  referralAge?: number | undefined;
  referralDate?: string | unknown[] | undefined;
  referralId?: unknown[] | undefined;
  referralStatus?: unknown[] | undefined;
  referralType?: string | undefined;
  resource?: PatientResource | undefined;
};

/**
 * What the referrals screen accepts.
 *
 * Two call sites supply disjoint halves, which is why everything is optional:
 * roster-wide mode (`home/members/index.tsx`) supplies `org`, `searchString`,
 * `onPatientSelect`, `activeItem`, `menuIndex`, `handleMenuItemClick` and no
 * `patientId`; member mode (`patient/index.tsx`) supplies `patientId`, `org`,
 * `activeTab`, `profileMutate` and none of the roster-wide set.
 *
 * `handleMenuItemClick` and `onPatientSelect` are invoked without a presence
 * check, but both are gated on roster-wide-only state
 * (`activeItem === 'referrals' && menuIndex === 5`, and `!patientId`), so
 * neither is reachable from member mode. The `!` assertions at those sites
 * record that the guard, not the type, is what makes them safe.
 */
type ReferralsProps = {
  /** Payer org; scopes the referral query. */
  org?: string | undefined;
  /** Present in member mode only; narrows the query to one member. */
  patientId?: string | undefined;
  /** Roster-wide free-text member filter; shares the query slot with `patientId`. */
  searchString?: string | undefined;
  /** Watched by an effect that refreshes when the tab becomes 'REFERRALS'. */
  activeTab?: string | undefined;
  /** Roster-wide only — see the SUSPECTED DEFECT note above. */
  handleMenuItemClick?: ((name: string) => void) | undefined;
  /** Roster-wide only; opens the chosen member. Guarded by `!props.patientId`. */
  onPatientSelect?: ((resource: PatientResource) => void) | undefined;
  /** Member mode only; revalidates the member's profile after a referral write. */
  profileMutate?: (() => void) | undefined;
  /** Accepted by the roster-wide caller and unread here. */
  activeItem?: string | undefined;
  /** Accepted by the roster-wide caller and unread here. */
  menuIndex?: number | undefined;
};

/**
 * The per-patient decoration merged onto each referral row.
 *
 * Built in the care-team `onSuccess` from a Patient bundle, then overwritten in
 * place with the care-team assignments (`OA`, `RD`) from the paired CareTeam
 * bundle, and finally spread over each {@link ReferralRow}.
 *
 * The text branch takes `patientPhone[0]`, but the email branch sends the WHOLE
 * `patientEmail` array as a message's `to:` — indexing it would change what is sent.
 */
type ReferralPatientRef = {
  OA: string | undefined;
  RD: string | undefined;
  patientName: string | undefined;
  resource: PatientResource;
  patientEmail: unknown[];
  patientPhone: unknown[];
};

function Referrals(props: ReferralsProps) {
  const authInfo = useAuthContext();
  const [tableData, setTableData] = useState<ReferralRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusvalue, setStatusValue] = useState('');
  const [dateValue, setDateValue] = useState('');
  const [urls, setUrls] = useState<string[]>([]);
  const [dateQuery, setDateQuery] = useState('');
  const [searchId, setSearchId] = useState('');
  const [referralTableData, setReferralTableData] = useState<ReferralRow[]>([]);
  const [referralType, _setReferralType] = useState('');
  const [_referralTypeOption, setReferalTypeOption] = useState<
    { key: string | undefined; value: string | undefined; text: string | undefined }[]
  >([]);

  // Indexed with a referral's `referralStatus` off the feed, so the key set is
  // open and an unrecognised status yields `undefined` — which Semantic's
  // `color` prop already treats as "no colour" rather than erroring.
  const REFERRAL_STATUS_COLORS_LITERAL = {
    CANCELLED: 'grey' as SemanticCOLORS,
    COMPLETED: 'green' as SemanticCOLORS,
    INELIGIBLE: 'black' as SemanticCOLORS,
    'IN-PROGRESS': 'orange' as SemanticCOLORS,
    NEW: 'blue' as SemanticCOLORS,
    OUTREACH: 'yellow' as SemanticCOLORS,
    PENDING: 'pink' as SemanticCOLORS,
    REJECTED: 'red' as SemanticCOLORS,
    UNREACHABLE: 'purple' as SemanticCOLORS,
  };
  const referralStatusColors: typeof REFERRAL_STATUS_COLORS_LITERAL &
    Record<string, SemanticCOLORS | undefined> = REFERRAL_STATUS_COLORS_LITERAL;

  useEffect(() => {
    if (props.activeTab === 'REFERRALS') {
      referralDataMutate(); //this will trigger SWR to re-Fetch
      //get plan definition and interventionActionId to get referral types
      if ((referralOrganizations as readonly (string | undefined)[]).includes(props.org)) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
        FHIRFetcher(`/PlanDefinition?identifier=${props.org}&status=active`, authInfo.token).then((plan) => {
          let planDefinition: PlanDefinition | undefined;
          let plannedInterventionActionId: string | undefined;
          let index: number | undefined;
          if (plan && plan.entry) {
            const planResource = plan.entry[0]!.resource as PlanDefinition | undefined;
            planDefinition = planResource;
            index = planResource?.action?.findIndex(
              (obj: PlanDefinitionAction) => obj.id === 'intervention'
            );
            if (index! >= 0) {
              plannedInterventionActionId = planResource?.action?.[index!]?.action?.[0]?.id;
            }
            if (planDefinition && plannedInterventionActionId) {
              const interventionSubActions = Intervene.getInterventionSubActions(
                planDefinition,
                plannedInterventionActionId,
              ) as { title?: string }[];
              if (interventionSubActions.length > 0) {
                setReferalTypeOption(
                  interventionSubActions.map((k) => {
                    return { key: k.title, value: k.title, text: k.title };
                  })
                );
              }
            }
          }
        });
      }
    }
     
  }, [props.activeTab]);

  /*
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. `handleMenuItemClick` is passed only
   * by the roster-wide `home/members` render; the member-scoped
   * `patient/index.tsx` render omits it. The effect's own condition tests
   * `props.menuIndex === 5`, which is a roster-wide-only value, so the two agree
   * today — but the guard is on the menu index rather than on the handler being
   * there, so any future caller reaching index 5 without one throws.
   */
  useEffect(() => {
    if (
      props.activeItem === 'referrals' &&
      props.menuIndex === 5 &&
      !(referralOrganizations as readonly (string | undefined)[]).includes(props.org)
    ) {
      props.handleMenuItemClick!('all members');
    }
  }, [props.activeItem, props.org]);

  useEffect(() => {
    dispatch({ type: 'CLEAR_FILTERS', data: referralTableData });
  }, [referralTableData]);
  // Indexed with the dropdown's selected value, a plain `string`, so the app's
  // open-key intersection applies: literal reads stay narrow, a computed read is
  // `number | undefined`.
  const DAYS_DICT_LITERAL = {
    TODAY: 0,
    'PAST 7 DAYS': 7,
    'PAST 14 DAYS': 14,
    'PAST 30 DAYS': 30,
    'PAST 60 DAYS': 60,
  };
  const daysDict: typeof DAYS_DICT_LITERAL & Record<string, number | undefined> = DAYS_DICT_LITERAL;

  async function onStatusChange(referral: ReferralRow, status: string) {
    const fhirReferral = buildFHIRReferral(referral.patientId, referral, status, authInfo.practitionerName, props.org);
    setLoading(true);
    let payload;
    if (props.org === 'HA') {
      payload = {
        to: referral.patientEmail,
        from: 'food@nourishedrx.com',
        patientId: referral.patientId,
        templateId: EMAIL_TEMPLATES['HA END OF REFERRAL'],
        subject: 'End of Referral',
        sender: authInfo.practitionerName,
        dynamicTemplateData: {
          memberName: referral.patientName,
        },
        text: 'auto',
      };
    }

    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);
    // Building Provenance
    const fhirBundle: FhirResource = prepareProvenancePayload(referral, status);

    FHIRUpdater('/Observation/' + referral.id, authInfo.token, fhirReferral).then(
      (_response) => {
        setLoading(false);
        referralDataMutate();

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        props.profileMutate && props.profileMutate();

        fhirProxyClient.send('post', '', fhirBundle).then(
          (_response) => {},
          (_error) => {
            // No operation needed on success/fail case
          }
        );
      },
      (error) => {
        setLoading(false);
        console.log('Failed to Update Status :' + error);
      }
    );
    if (status === 'COMPLETED' && payload) {
      await SendGridPoster(`/hooks/mail`, authInfo.token, payload).then(
        () => {
          console.log('End of Referral email sent');
        },
        () => {
          console.log('End of Referral email failed');
        }
      );
      if (referral.patientPhone?.[0]) {
        const textPayload = {
          patientId: referral.patientId,
          subject: 'End of Referral',
          sender: authInfo.practitionerName,
          phoneNumber: ('+1' + (referral.patientPhone?.[0] as string)).replaceAll('-', ''),
          message: REFERRAL_SUBJECT_LINES['HA END OF REFERRAL'],
          templateName: TEXT_MESSAGE_TEMPLATES_PINPOINT['HA END OF REFERRAL'],
          text: 'auto',
        };
        await SendGridPoster(`/hooks/text`, authInfo.token, textPayload).then(
          () => {
            console.log('End of Referral text sent');
          },
          () => {
            console.log('End of Referral text failed');
          }
        );
      }
    }
  }

  function prepareProvenancePayload(referral: ReferralRow, status: string) {
    const bundleEntries: BundleEntry[] = [];
    const fhirBundle: FhirResource = FHIRSDOHUtils.buildEmptyBundle('transaction');
    const orgRef = {
      code: props.org && props.org.toUpperCase(),
      display: props.org && props.org.toLowerCase(),
    };

    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Observation/${referral.id}`,
      {
        reference: 'Practitioner/' + authInfo.practitionerId,
        display: authInfo.practitionerName,
      }, //eg. {Practitioner/oa2,johnsmith},
      referral.patientId,
      'Update',
      `Referral status has been changed to ` +
        status +
        ' for Referral: ' +
        (referral.referralId as unknown as string) +
        '-' +
        referral.referralType,
      orgRef //eg. {Organization/HUMANA,humana}
    );

    bundleEntries.push(FHIRSDOHUtils.buildResourceEntry(provenance, false)); //Provenance
    //now post bundle request
    fhirBundle.entry = bundleEntries;

    return fhirBundle;
  }

  const [state, dispatch] = React.useReducer(sortReducer<ReferralRow>, {
    column: null,
    data: tableData,
    direction: null,
  });
  const { column, data, direction } = state;

  const sliceIntoChunks = function <T>(arr: T[], chunkSize: number) {
    const res: T[][] = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      const chunk = arr.slice(i, i + chunkSize);
      res.push(chunk);
    }
    return res;
  };

  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    // The referral search Bundle. Only `entry[].resource` is read, and that is
    // handed straight to `FHIRUtils.parseFHIRreferral`, so the entry element is
    // named as the FHIR resource it carries rather than restated field by field.
    onSuccess: (data: Fetched<Bundle>, _key: unknown, _config: unknown) => {
      const patientIds: string[] = [];
      setTableData([]);
      const tempTableData: ReferralRow[] = [];

      if (data && data.entry && data.entry.length > 0) {
        // let referralsList = data.entry.splice(0,1);
        const referralsList = data.entry;
        // if (statusvalue.length > 0) {
        //   referralsList = referralsList.filter((r) =>
        //     r.resource.identifier.find(
        //       (i) =>
        //         i.system === 'https://projectwell.io/fhir/identifiers/referral-status' &&
        //         i.value === statusvalue &&
        //         i.system === 'https://projectwell.io/fhir/orgid' &&
        //         i.value === props.org
        //     )
        //   );
        // }

        referralsList.forEach((entry: BundleEntry) => {
          const patientRef = (entry.resource as Observation).subject!.reference!;
          const patientId = patientRef.substring(patientRef.indexOf('/') + 1);
          patientIds.push(patientId);

          const flatReferral = FHIRUtils.parseFHIRreferral(entry.resource as Observation);
          setTableData((result) => {
            return [...result, flatReferral];
          });
          tempTableData.push(flatReferral);
        });
        setReferralTableData((_prev) => tempTableData);

        const uniquePatients = _.uniq(patientIds);
        const endPoints: string[] = [];
        const patientChunks = sliceIntoChunks(uniquePatients, 99);

        patientChunks.forEach((chunk) => {
          endPoints.push(`/Patient/?organization=${props.org}&_id=${chunk.join(',')}`);
          endPoints.push(`/CareTeam/?subject=${chunk.join(',')}`);
        });

        setUrls((_prev) => [...endPoints]);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
        careTeamMutate();
      }
    },
  };

  const careTeamOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    /*
     * `MultiFetcher` resolves one Bundle per URL, and this handler walks them in
     * pairs — the `patient` flag flips each iteration, so even indices are
     * Patient bundles and odd ones CareTeam bundles. That alternation is the
     * "order of urls in this array matter!" contract the fetch site relies on.
     */
    onSuccess: (dataset: Awaited<ReturnType<typeof MultiFetcher<Bundle>>>, _key: unknown, _config: unknown) => {
      if (dataset?.length) {
        // let chunkLength = dataset.length*2;
        let patient = true;
        const patientsDataset: { entry: PatientEntry[] } = { entry: [] };
        const careTeamDataset: { entry: { resource: CareTeam }[] } = { entry: [] };
        // A slot is `undefined` only for an empty URL, which this key never produces.
        // Asserted rather than guarded: a `?.` would silently drop a bundle and
        // desynchronise the patient/careTeam alternation the loop depends on.
        dataset.forEach((element) => {
          if (patient) {
            if (element!.entry) {
              patientsDataset.entry = [...(element!.entry as PatientEntry[]), ...patientsDataset.entry];
            }
          } else {
            if (element!.entry) {
              careTeamDataset.entry = [
                ...(element!.entry as { resource: CareTeam }[]),
                ...careTeamDataset.entry,
              ];
            }
          }
          patient = !patient;
        });

        /*
         * Keyed by patient id, which is `string | undefined` on a FHIR Patient —
         * a resource without one writes a literal `'undefined'` key here. Open
         * key set, so the read below is `… | undefined` and the existing
         * `patientsRef[…]` truthiness check is what guards it.
         */
        const patientsRef: Record<string, ReferralPatientRef | undefined> = {};
        patientsDataset?.entry?.forEach((item) => {
          const name = FHIRUtils.parseFHIRPatientName(item.resource);
          const email = FHIRUtils.parseFHIRPatientEmail(item.resource);
          const phone = FHIRUtils.parseFHIRPatientPhone(item.resource);
          patientsRef[item.resource.id] = {
            OA: '',
            RD: '',
            patientName: name,
            resource: item.resource,
            patientEmail: email,
            patientPhone: phone,
          };
        });

        careTeamDataset?.entry?.forEach((item) => {
          const careTeamReference = FHIRUtils.parseFHIRCareTeam(item.resource);
          // Hoisted so the presence test narrows the entry that is then written;
          // repeated subscripts of the same key narrow nothing, which is what the
          // `!`s were standing in for.
          const patientRef = patientsRef?.[mapKey(careTeamReference.patientId)];
          if (patientRef) {
            patientRef['OA'] = careTeamReference.OA;
            patientRef['RD'] = careTeamReference.RD;
          }
        });

        const referralObject: ReferralRow[] = [];

        referralTableData.forEach((ref) => {
          referralObject.push({ ...ref, ...patientsRef[mapKey(ref.patientId)] });
        });

        setReferralTableData(referralObject);
      }
    },
  };

  const {
    error: careTeamError,
    mutate: careTeamMutate,
    isValidating: careTeamDatasetLoading,
  } = useSWR<void | (Bundle | undefined)[], ApiFetchError>(
    [authInfo.token, ...urls],
    MultiFetcher,
    careTeamOptions,
  );

  //fetch referral data
  const {
    data: referralData,
    error: referralError,
    mutate: referralDataMutate,
    isValidating: referralDatasetLoading,
  } = useSWR(
    // [`/referral`, authInfo.token],
    [
      `/Observation/?code=${REFERRAL_OBSERVATION_CODE}&identifier=${props.org}${
        statusvalue ? ':::' + statusvalue : ''
      }&_sort=-date&_count=500${
        props.patientId || props.searchString ? '&patient=' + (props.patientId || props.searchString) : ''
      }${searchId ? '&_content:exact=' + searchId : ''}${
        dateQuery ? dateQuery : ''
      }&identifier:not=https://projectwell.io/fhir/identifiers/referral-status|ENTERED-IN-ERROR`,
      authInfo.token,
    ],
    FHIRFetcher<fhir4.Bundle>,
    options
  ) as {
    data: Fetched<Bundle>;
    error: ApiFetchError;
    mutate: () => void;
    isValidating: boolean;
  };

  const _getDisplay = (code: string) => {
    const arr = code.toLowerCase().split('-');
    for (let i = 0; i < arr.length; i++) {
      arr[i] = arr[i]!.charAt(0).toUpperCase() + arr[i]!.slice(1);
    }
    const str = arr.join(' ');
    return str;
  };

  const _generateNewReferralId = (referralId: string | null | undefined) => {
  /*
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. `org` is optional on this component
   * and `.toString()`/`.toUpperCase()` throw on absence. `_generateNewReferralId`
   * is currently unreferenced, but the SWR key below is evaluated on every render,
   * so a mount without an org fails before the referral-id query is issued.
   */
    let referralIdPrefix = props.org!.toString();
    if (props.org!.toString() === 'WCNC-ARPA-PRX') {
      referralIdPrefix = referralIdPrefix + (referralType ? '-' + REFERRAL_ID_PREFIX[referralType.toUpperCase()] : '');
    }
    if (referralId === null || referralId === undefined || referralId.length === 0) {
      referralId = referralIdPrefix + moment().format('YY').toString() + '0';
    }
    let seqNumber = referralId.slice(referralIdPrefix.length + 2).toString(); // Slice the referral id and get only the last 5 digit part
    seqNumber = (parseInt(seqNumber) + 1).toString(); // Increment by 1
    while (seqNumber.length < 5) seqNumber = '0' + seqNumber;
    return referralIdPrefix + moment().format('YY') + seqNumber;
  };

  const fhirReferralOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  };

  const { data: _fhirReferralData, mutate: _fhirReferralDataMutate } = useSWR(
    [`/food/referral-id?organization=${props.org!.toUpperCase()}`, authInfo.token],
    FoodFetcher,
    fhirReferralOptions
  );

  const profileOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  };

  const { data: _profileData, mutate: _profileDataMutate } = useSWR<Fetched<Bundle>, ApiFetchError>(
    [
      `/Observation/?identifier=${props.org}&code=REFERRAL-TYPE&_sort=-_lastUpdated&_count=1&identifier:not=https://projectwell.io/fhir/identifiers/referral-status|ENTERED-IN-ERROR`,
      authInfo.token,
    ],
    profileOptions
  );

  const openPatient = (resource: PatientResource | undefined) => {
    // `onPatientSelect` is roster-wide only; the `!props.patientId` test is what
    // makes this unreachable in member mode. See the props doc above.
    if (!props.patientId && resource) props.onPatientSelect!(resource);
  };

  const handleSearchById = (e: React.MouseEvent<HTMLElement>, searchProps: { value?: string }) => {
    setSearchId(searchProps.value!);
  };

  const resetFilters = () => {
    setSearchId('');
    setStatusValue('');
    setDateValue('');
    setDateQuery('');
  };

  const handleDateChange = (e: React.SyntheticEvent<HTMLElement>, selectprops: DropdownProps) => {
    // `daysDict` is keyed by the literal option labels; a value outside that set
    // yields `undefined`, which the `=== 0` test below treats as the "range"
    // branch and hands to `moment().subtract(undefined, 'day')`. Existing
    // behaviour — the dropdown only offers the five known labels.
    const numDays = daysDict[selectprops.value as string];
    let query: string;
    if (numDays === 0) {
      query = `&date=eq` + moment().format('YYYY-MM-DD');
    } else {
      const sa = moment().subtract(numDays, 'day').format('YYYY-MM-DD');
      query = `&date=ge` + sa;
    }
    setDateQuery(query);
    setDateValue(selectprops.value as string);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Form autoComplete="off">
          <Form.Group>
            <Form.Input
              placeholder="Search by ID"
              icon={<Icon name="search" onClick={handleSearchById} />}
              value={searchId}
              onChange={(e, searchProps) => setSearchId(searchProps.value)}
              name="searchId"
            />
            <Form.Field
              control={Select}
              selectOnBlur={false}
              name="period"
              placeholder="Filter by Date"
              value={dateValue}
              options={['TODAY', 'PAST 7 DAYS', 'PAST 14 DAYS', 'PAST 30 DAYS', 'PAST 60 DAYS'].map((k) => {
                return { key: k, value: k, text: k };
              })}
              onChange={handleDateChange}
            />
            <Form.Field
              control={Select}
              selectOnBlur={false}
              name="category"
              placeholder="Filter by Status"
              value={statusvalue}
              options={Object.keys(referralStatusColors).map((x) => {
                return { key: x, text: x.toString().toUpperCase(), value: x };
              })}
              onChange={(e: React.SyntheticEvent<HTMLElement>, statusprops: DropdownProps) =>
                setStatusValue(statusprops.value as string)
              }
            />
            <Form.Button content="Clear" color="blue" onClick={resetFilters} />
          </Form.Group>
        </Form>
      </div>
      {referralDatasetLoading && <Loader active inline="centered" />}
      {referralData && !referralData.entry && <Message warning header="No Referrals Exists!!!" />}
      {referralError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          {/* SUSPECTED DEFECT: renders empty on the string-rejection branch. See `ApiFetchError`. */}
          <p>{(referralError as { message?: string }).message}</p>
        </Message>
      )}
      <div className="table-div">
        {!referralDatasetLoading && !referralError && !careTeamError && (
          <Table sortable celled padded singleLine>
            <Dimmer inverted active={loading}>
              <Loader />
            </Dimmer>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell
                  width={1}
                  {...sortDirection(column === 'referralId' ? direction : null)}
                  onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'referralId' })}
                >
                  Referral Id (Total: {data ? data.length : '0'})
                </Table.HeaderCell>
                {!props.patientId && (
                  <Table.HeaderCell
                    width={1}
                    {...sortDirection(column === 'patientName' ? direction : null)}
                    onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'patientName' })}
                  >
                    Member Name
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell
                  width={1}
                  {...sortDirection(column === 'referralType' ? direction : null)}
                  onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'referralType' })}
                >
                  Referral Type
                </Table.HeaderCell>
                <Table.HeaderCell
                  width={1}
                  {...sortDirection(column === 'referralDate' ? direction : null)}
                  onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'referralDate' })}
                >
                  Referral Date
                </Table.HeaderCell>
                <Table.HeaderCell
                  width={1}
                  {...sortDirection(column === 'referralAge' ? direction : null)}
                  onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'referralAge' })}
                >
                  Referral Age
                </Table.HeaderCell>

                <Table.HeaderCell
                  width={1}
                  {...sortDirection(column === 'interventions' ? direction : null)}
                  onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'interventions' })}
                >
                  Interventions
                </Table.HeaderCell>
                <Table.HeaderCell
                  width={1}
                  {...sortDirection(column === 'OA' ? direction : null)}
                  onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'OA' })}
                >
                  WA
                </Table.HeaderCell>
                <Table.HeaderCell
                  width={1}
                  {...sortDirection(column === 'RD' ? direction : null)}
                  onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'RD' })}
                >
                  RD
                </Table.HeaderCell>
                <Table.HeaderCell
                  width={2}
                  {...sortDirection(column === 'referralStatus' ? direction : null)}
                  onClick={() => dispatch({ type: 'CHANGE_SORT', column: 'referralStatus' })}
                >
                  Referral Action
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {referralData &&
                referralData.entry &&
                data.map((referral: ReferralRow) => (
                  <Table.Row key={referral.id} singleLine>
                    <Table.Cell>
                      <Label color="brown">{referral.referralId}</Label>
                    </Table.Cell>
                    {!props.patientId && (
                      <Table.Cell>
                        {careTeamDatasetLoading ? (
                          <Loader inline active size="small" />
                        ) : (
                          <a
                            style={{
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                            onClick={() => openPatient(referral?.resource)}
                          >
                            {referral?.patientName}
                          </a>
                        )}
                      </Table.Cell>
                    )}
                    <Table.Cell>{referral.referralType}</Table.Cell>
                    {/* <Table.Cell>{referral.referralDate}</Table.Cell> */}
                    <Table.Cell>{referral.referralDate}</Table.Cell>

                    <Table.Cell>{referral.referralAge} days</Table.Cell>
                    <Table.Cell>{referral?.interventions}</Table.Cell>
                    <Table.Cell>
                      {careTeamDatasetLoading ? <Loader inline active size="tiny" /> : referral?.OA}
                    </Table.Cell>
                    <Table.Cell>
                      {careTeamDatasetLoading ? <Loader inline active size="tiny" /> : referral?.RD}
                    </Table.Cell>
                    <Table.Cell>
                      {/* An unlisted status yields `undefined`, which Semantic treats as an
                          absent `color`; a default would paint it a real colour. */}
                      <Button.Group
                        size="small"
                        color={referralStatusColors[mapKey(referral.referralStatus)] as SemanticCOLORS}
                      >
                        <Popup
                          trigger={<Button>{referral.referralStatus![0]}</Button>}
                          content={
                            <>
                              <i>{referral.lastUpdatedAge}</i> by <b>{referral.lastUpdatedBy}</b>
                            </>
                          }
                        />
                        <Dropdown
                          className="button icon"
                          floating
                          selectOnBlur={false}
                          onChange={(e, selectProps) =>
                            onStatusChange(referral, selectProps.value as string)
                          }
                          options={Object.keys(referralStatusColors).map((x) => {
                            return { key: x, text: x, value: x };
                          })}
                          trigger={<></>}
                        />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </div>
  );
}

/**
 * Builds the FHIR ServiceRequest body for a referral status change.
 *
 * `orgId` is `string | null | undefined` because it is `props.org` threaded
 * through from a caller that may pass neither.
 */
function buildFHIRReferral(
  patientId: string | undefined,
  referral: ReferralRow,
  status: string,
  modifiedBy: string,
  orgId: string | null | undefined
) {
  return {
    code: {
      coding: [
        {
          code: 'REFERRAL-TYPE',
          display: 'Referral Type',
          system: 'https://projectwell.io/fhir/preference-codes',
        },
      ],
    },
    effectivePeriod: {
      start: moment
        .tz(referral.referralDate as string, 'YYYY-MM-DD hh:mm a', moment.tz.guess())
        .tz('America/New_York')
        .format('YYYY-MM-DDTHH:mm:ss+00:00'),
    },
    id: referral.id,
    identifier: [
      {
        system: 'https://projectwell.io/fhir/identifiers/last-modified-by',
        value: modifiedBy,
      },
      {
        system: 'https://projectwell.io/fhir/identifiers/referral-id',
      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. `referralId` and `interventions`
       * are both optional on a referral row, and `.toString()` throws on absence
       * rather than coercing (the reason `utils/mapKey` exists). Saving a referral
       * whose id or intervention list never came back from the feed therefore
       * fails while building the identifier list, before anything is posted.
       */
        value: referral.referralId!.toString(),
      },
      {
        system: 'https://projectwell.io/fhir/identifiers/referral-status',
        value: status,
      },
      {
        system: 'https://projectwell.io/fhir/identifiers/interventions',
        value: referral.interventions!.toString(),
      },
      {
        system: 'https://projectwell.io/fhir/orgid',
        value: orgId,
      },
    ],
    resourceType: 'Observation',
    status: 'final',
    subject: {
      reference: 'Patient/' + patientId,
    },
    valueString: referral.referralType,
  };
}

export default Referrals;
